import { useRef, useEffect } from 'react';

const SERVICES = [
  {
    tag: '01',
    icon: 'ri-router-line',
    title: 'オフィスIT機器・ネットワーク構築',
    subtitle: 'Office IT & Network',
    desc: 'セキュリティを前提とした、安全で快適なオフィス環境を構築します。ITとセキュリティを切り離さず、トータルで最適化します。',
    items: ['PC・周辺機器', '複合機', '防犯カメラ', '社内LAN／ネットワーク施工', 'Wi-Fi環境構築'],
    img: 'https://public.readdy.ai/ai/img_res/3f360dade643cdcdbab3134a636b74dd.jpg',
  },
  {
    tag: '02',
    icon: 'ri-shield-keyhole-line',
    title: '情報セキュリティ対策',
    subtitle: 'Information Security',
    desc: '企業の情報資産を守るため、現実的かつ運用しやすいセキュリティ対策をご提案します。「何も起きてない」は安全とは限りません。見えないリスクを"見える化"することから始めます。',
    items: ['UTM', 'セキュアブラウザ', 'ウィルス対策ソフト', '不正アクセス対策', '情報漏洩対策'],
    img: 'https://readdy.ai/api/search-image?query=financial%20documents%20spreadsheets%20calculator%20on%20clean%20desk%2C%20professional%20accountant%20tax%20advisor%20working%2C%20modern%20office%20environment%2C%20natural%20light%2C%20organized%20and%20precise%2C%20soft%20blue%20and%20white%20tones&width=600&height=400&seq=srv002&orientation=landscape',
  },
  {
    tag: '03',
    icon: 'ri-ai-generate',
    title: 'IT・DX支援',
    subtitle: 'IT & DX Support',
    desc: 'ITを「導入して終わり」にせず、業務に活用するところまでサポートします。HP作成からAI・DX活用まで、ビジネスのデジタル化を幅広く支援します。',
    items: ['HP作成', 'AI活用コンサルティング', 'DXコンサルティング', '業務効率化支援'],
    img: 'https://readdy.ai/api/search-image?query=modern%20digital%20transformation%20technology%20business%2C%20laptop%20with%20data%20dashboards%20analytics%2C%20clean%20tech%20office%20environment%2C%20minimalist%20design%2C%20sky%20blue%20and%20neutral%20tones%2C%20professional%20setting&width=600&height=400&seq=srv004&orientation=landscape',
  },
];

export default function ServicesSection() {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = sectionRefs.current.map((el) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) { el.classList.add('animate-in'); obs.disconnect(); }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <section id="services" className="py-28 px-10 lg:px-20 bg-[#F8FCFF]">
      <div
        ref={(el) => { sectionRefs.current[0] = el; }}
        className="opacity-0 translate-y-8 transition-all duration-700 text-center mb-16"
      >
        <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>SERVICES</p>
        <h2 className="text-3xl lg:text-4xl font-light text-[#1A2E3A]" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
          提供サービス
        </h2>
      </div>

      <div className="flex flex-col gap-20">
        {SERVICES.map((service, i) => (
          <div
            key={service.tag}
            ref={(el) => { sectionRefs.current[i + 1] = el; }}
            className={`opacity-0 translate-y-8 transition-all duration-700 flex flex-col ${
              i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
            } gap-12 items-center max-w-5xl mx-auto w-full`}
          >
            <div className="lg:w-5/12 w-full">
              <div className="relative">
                <div className="w-full h-64 rounded-2xl overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-14 h-14 bg-[#2B8DB8] rounded-xl flex items-center justify-center">
                  <span className="text-white/70 text-xs tracking-widest" style={{ fontFamily: "'Inter', sans-serif" }}>{service.tag}</span>
                </div>
              </div>
            </div>
            <div className="lg:w-7/12 w-full">
              <p className="text-[#2B8DB8] text-xs tracking-[0.3em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{service.subtitle}</p>
              <h3 className="text-2xl lg:text-3xl font-light text-[#1A2E3A] mb-4 leading-snug" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
                {service.title}
              </h3>
              <p className="text-[#5A7A8A] text-sm leading-loose tracking-wider mb-6">{service.desc}</p>
              <div className="grid grid-cols-2 gap-2">
                {service.items.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-[#1A2E3A] text-xs tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2B8DB8] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
